/* @flow */

import type { Node as ReactNode } from "react";

import React, { useContext, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { usePreserveScrollPosition } from "react-router-on-navigation";
import AppHeader from "components/AppHeader";
import { Helmet } from "react-helmet-async";
import { useTranslate } from "@awardit/react-use-translate";
import { StoreInfoContext } from "entrypoint/shared";
import { ViewModeData, CmsPageData, AdventCalendarData } from "data";
import { UiProvider, useUi } from "helpers/ui";
import { MODE } from "state/view-mode";
import { useHistory, useLocation } from "react-router";
import useCookie from "helpers/use-cookie";
import CookieConsent from "components/CookieConsent";
import { NotificationBar, AppFooter, Button, SkipToContent } from "@crossroads/ui-components";
import Logo from "components/AppHeader/Logo";
import { parseParams } from "helpers/location-search-string";

import cn from "classnames";
import styles from "./styles.scss";

type Props = {
  showMiniBanner: boolean,
  children: ReactNode,
};

const App = ({ showMiniBanner, children }: Props): React$Node => {
  const container = useRef(null);
  const history = useHistory();
  const t = useTranslate();
  const location = useLocation();
  const onCheckout = (location ? location.pathname : "").includes("checkout");
  const { info } = useContext(StoreInfoContext);
  const cookieOpts = { domain: `.${typeof window !== "undefined" ? window.location.host.replace(/:\d+$/, "") : ""}`, expires: 3650 };
  const [consent, setConsent, consentRead] = useCookie("cookie-consent", "initial", cookieOpts);
  const [toggleCookieDialogue, setToggleCookieDialogue] = useState(false);
  const [hideNotificationBar, setHideNotificationBar] = useState(false);
  const { password, testDate } = parseParams(location.search);

  usePreserveScrollPosition(history);

  const footerContentTerms = () => (
    <div className={styles.termsField}>
      <div>
        <Link className={styles.link} to="/terms">
          {t("FOOTER.TERMS")}
        </Link>
        &nbsp;|&nbsp;
        <Link className={styles.link} to="/privacy-policy">
          {t("FOOTER.PRIVACY_POLICY")}
        </Link>
        &nbsp;|&nbsp;
        <Button
          aria-expanded={toggleCookieDialogue ?? "true"}
          aria-controls="cookieDialogue"
          className={styles.link}
          onClick={() => setToggleCookieDialogue(true)}
        >
          {t("FOOTER.COOKIES")}
        </Button>
        &nbsp;|&nbsp;
        <Link className={styles.link} to="/accessibility">
          {t("FOOTER.ACCESSIBILITY")}
        </Link>
      </div>
      <p dangerouslySetInnerHTML={{ __html: t("FOOTER.COPYRIGHT", { year: new Date().getFullYear() }) }} />
    </div>
  );

  return (
    <ViewModeData.Provider>
      <CmsPageData.Provider url="cookie-consent">
        <AdventCalendarData.Provider
          testDate={typeof testDate === "string" ? testDate : null}
          password={typeof password === "string" ? password : null}
        >
          <UiProvider>
            <div
              ref={container} className={cn(
                styles.block,
                { [styles.onCheckout]: onCheckout }
              )}
            >
              <Helmet
                titleTemplate={`${info.titlePrefix || ""} %s ${info.titleSuffix || ""}`}
                link={[
                  { rel: "icon", type: "image/png", size: "16x16", href: "/assets/browser/favicon-16x16.png" },
                  { rel: "icon", type: "image/png", size: "32x32", href: "/assets/browser/favicon-32x32.png" },
                  { rel: "manifest", href: "/assets/browser/site.webmanifest" },
                  { rel: "mask-icon", href: "/assets/browser/safari-pinned-tab.svg", color: "#da2911" },
                ]}
                meta={[
                  { name: "msapplication-TileColor", content: "#ffffff" },
                  { name: "theme-color", content: "#ffffff" },
                ]}
                htmlAttributes={{ lang: info.locale.slice(0, 2) }}
              />
              <SkipToContent />
              {consentRead &&
              <div className={styles.notices}>
                <CookieConsent
                  country={info.defaultCountry}
                  isVisible={consentRead && (consent === "initial" || toggleCookieDialogue)}
                  consent={consent}
                  setConsent={setConsent}
                  consentRead={consentRead}
                  setToggleCookieDialogue={setToggleCookieDialogue}
                />
              </div>
              }
              <div className={styles.top}>
                <AppHeader showMiniBanner={showMiniBanner} />

                <div
                  className={cn(styles.height, { [styles.headerBannerHeight]: showMiniBanner })}
                />

                <main id="main">
                  {children}
                </main>
              </div>

              {(!onCheckout) && (
                <AppFooter
                  hasVisibleHelpColumn
                  className={styles.footer}
                  activeLinks={["start", "categories", "brands", "search"]}
                  contentBranding={() => <Logo className={styles.logo} />}
                  contentTerms={footerContentTerms}
                  useUi={useUi}
                  MODE={MODE}
                />
              )}

              {info.popUp && info.popUp.body !== "" && !hideNotificationBar &&
                <NotificationBar
                  className={styles.notificationBar}
                  text={info.popUp.body}
                  closeBtnTitle={t("NOTIFICATIONBAR.CLOSE")}
                  onClose={() => setHideNotificationBar(true)}
                />
              }
            </div>
          </UiProvider>
        </AdventCalendarData.Provider>
      </CmsPageData.Provider>
    </ViewModeData.Provider>
  );
};

export default App;
